import { useMemo } from 'react';

export type VisualType =
  | 'matrix3x3'
  | 'matrix4x4'
  | 'matrix2x2'
  | 'shapeSequence'
  | 'numberSequence'
  | 'numberGrid'
  | 'rotation'
  | 'arrowSequence'
  | 'blockGrid'
  | 'overlap'
  | 'overlapMatrix'
  | 'overlapMatrix3D'
  | 'solidView'
  | 'cubeStack'
  | 'spokeMatrix'
  | 'gridLineMatrix'
  | 'stripeMatrix'
  | 'xorLineMatrix'
  | 'spokeShape'
  | 'gridLineShape'
  | 'stripeShape'
  | 'xorLineShape'
  | 'compositeMatrix'
  | 'compositeShape'
  | 'clockHands'
  | 'solidSequence'
  | 'solidMatrix'
  | 'pokerRotation'
  | 'pokerSolid'
  | null;

export interface VisualData {
  matrix?: (string | null)[][];
  steps?: string[];
  numbers?: (string | number)[];
  rotations?: number[];
  items?: string[];
  label?: string;
  grid?: number[][];
  heights?: number[][];
  cells?: (number[][] | null)[];
  solids?: (number[][] | null)[];
  solid?: number[][];
  spokes?: (number | null)[];
  crosses?: ({ h: number; v: number } | null)[];
  stripes?: ({ shape: string; dir: string } | null)[];
  xorLines?: ({ shape: string; lines: string[] } | null)[];
  spokeCount?: number;
  cross?: { h: number; v: number };
  stripe?: { shape: string; dir: string };
  xorLine?: { shape: string; lines: string[] };
  composites?: ({ frame: string; lines: string[] } | null)[];
  composite?: { frame: string; lines: string[] };
  clocks?: ({ hour: number; minute: number } | null)[];
  solids?: (string | null)[];
  faces?: string[];
  top?: string;
  rotations?: number[];
  step?: number;
  pokerSolid?: string;
}

interface Props {
  visualType?: VisualType;
  visualData?: VisualData;
  compact?: boolean;
}

export default function IqVisualRenderer({ visualType, visualData, compact = false }: Props) {
  if (!visualType || !visualData) return null;

  let content: React.ReactNode = null;
  switch (visualType) {
    case 'matrix3x3':
      content = <Matrix3x3 data={visualData} />;
      break;
    case 'matrix4x4':
      content = <Matrix4x4 data={visualData} />;
      break;
    case 'matrix2x2':
      content = <Matrix2x2 data={visualData} />;
      break;
    case 'shapeSequence':
      content = <ShapeSequence data={visualData} />;
      break;
    case 'numberSequence':
      content = <NumberSequence data={visualData} />;
      break;
    case 'numberGrid':
      content = <NumberGrid data={visualData} />;
      break;
    case 'rotation':
      content = <Rotation data={visualData} />;
      break;
    case 'arrowSequence':
      content = <ArrowSequence data={visualData} />;
      break;
    case 'blockGrid':
      content = <BlockGrid data={visualData} compact={compact} />;
      break;
    case 'overlap':
      content = <Overlap data={visualData} compact={compact} />;
      break;
    case 'overlapMatrix':
      content = <OverlapMatrix data={visualData} />;
      break;
    case 'overlapMatrix3D':
      content = <OverlapMatrix3D data={visualData} />;
      break;
    case 'spokeMatrix':
      content = <SpokeMatrix data={visualData} />;
      break;
    case 'gridLineMatrix':
      content = <GridLineMatrix data={visualData} />;
      break;
    case 'stripeMatrix':
      content = <StripeMatrix data={visualData} />;
      break;
    case 'xorLineMatrix':
      content = <XorLineMatrix data={visualData} />;
      break;
    case 'spokeShape':
      content = <SpokeShape data={visualData} />;
      break;
    case 'gridLineShape':
      content = <GridLineShape data={visualData} />;
      break;
    case 'stripeShape':
      content = <StripeShape data={visualData} />;
      break;
    case 'xorLineShape':
      content = <XorLineShape data={visualData} />;
      break;
    case 'compositeMatrix':
      content = <CompositeMatrix data={visualData} />;
      break;
    case 'compositeShape':
      content = <CompositeShape data={visualData} />;
      break;
    case 'clockHands':
      content = <ClockSequence data={visualData} compact={compact} />;
      break;
    case 'solidSequence':
      content = <SolidSequence data={visualData} compact={compact} />;
      break;
    case 'solidMatrix':
      content = <SolidMatrix data={visualData} />;
      break;
    case 'pokerRotation':
      content = <PokerRotation data={visualData} compact={compact} />;
      break;
    case 'pokerSolid':
      content = <PokerSolid data={visualData} compact={compact} />;
      break;
    case 'solidView':
      content = <SolidView voxels={visualData.solid || []} />;
      break;
    case 'cubeStack':
      content = <CubeStack data={visualData} compact={compact} />;
      break;
    default:
      content = null;
  }

  if (compact) return <>{content}</>;
  return <div className="mb-6">{content}</div>;
}

/* ── 4×4 매트릭스 ── */
function Matrix4x4({ data }: { data: VisualData }) {
  const cells = data.matrix || Array(4).fill(Array(4).fill(null));
  return (
    <div className="flex flex-col items-center gap-1">
      {data.label && <span className="text-sm text-foreground-500 mb-2">{data.label}</span>}
      <div
        className="grid gap-0 border-2 border-foreground-800"
        style={{ gridTemplateColumns: 'repeat(4, 56px)' }}
      >
        {cells.flat().map((cell, idx) => (
          <Cell key={idx} value={cell} size="md" />
        ))}
      </div>
    </div>
  );
}

/* ── 3×3 매트릭스 ── */
function Matrix3x3({ data }: { data: VisualData }) {
  const cells = data.matrix || Array(3).fill(Array(3).fill(null));
  return (
    <div className="flex flex-col items-center gap-1">
      {data.label && <span className="text-sm text-foreground-500 mb-2">{data.label}</span>}
      <div
        className="grid gap-0 border-2 border-foreground-800"
        style={{ gridTemplateColumns: 'repeat(3, 72px)' }}
      >
        {cells.flat().map((cell, idx) => (
          <Cell key={idx} value={cell} size="lg" />
        ))}
      </div>
    </div>
  );
}

/* ── 2×2 매트릭스 ── */
function Matrix2x2({ data }: { data: VisualData }) {
  const cells = data.matrix || Array(2).fill(Array(2).fill(null));
  return (
    <div className="flex flex-col items-center gap-1">
      {data.label && <span className="text-sm text-foreground-500 mb-2">{data.label}</span>}
      <div
        className="grid gap-0 border-2 border-foreground-800"
        style={{ gridTemplateColumns: 'repeat(2, 80px)' }}
      >
        {cells.flat().map((cell, idx) => (
          <Cell key={idx} value={cell} size="lg" />
        ))}
      </div>
    </div>
  );
}

/* ── 셀 컴포넌트 ── */
function Cell({ value, size }: { value: string | null; size: 'sm' | 'md' | 'lg' }) {
  const isBlank = value === '?' || value === null || value === '';
  const sizeClasses = useMemo(() => {
    switch (size) {
      case 'sm':
        return 'w-10 h-10 text-lg';
      case 'md':
        return 'w-14 h-14 text-xl';
      case 'lg':
        return 'w-[72px] h-[72px] text-2xl';
    }
  }, [size]);

  if (isBlank) {
    return (
      <div
        className={`${sizeClasses} flex items-center justify-center border border-foreground-800 bg-primary-50/60 font-bold text-primary-600`}
      >
        ?
      </div>
    );
  }

  return (
    <div
      className={`${sizeClasses} flex items-center justify-center border border-foreground-800 bg-background-50 font-semibold text-foreground-900 whitespace-pre-line leading-tight text-center px-1`}
    >
      {renderCellContent(String(value))}
    </div>
  );
}

/* ── 셀 내용 렌더링 ── */
function renderCellContent(value: string) {
  const shapeChars = ['○', '●', '△', '▲', '▽', '▷', '◁', '□', '■', '◇', '◆', '◈', '◉', '⊙', '◐', '◑', '⬠', '⬡'];
  const arrowChars = ['↑', '→', '↓', '←', '↗', '↘', '↙', '↖'];
  const lineChars = ['│', '─', '┌', '┐', '└', '┘', '├', '┤', '┬', '┴', '┼', '+', '×', '╱', '╲', '✕'];

  const firstChar = value.charAt(0);
  const allSameShape = value.length > 1 && value.split('').every((c) => c === firstChar);

  if (shapeChars.includes(firstChar) && (allSameShape || value.length === 1)) {
    const count = value.length;
    const size = count >= 4 ? 'text-base' : count === 3 ? 'text-xl' : count === 2 ? 'text-2xl' : 'text-3xl';
    return <span className={`${size} tracking-tighter leading-none whitespace-nowrap`}>{value}</span>;
  }

  if (arrowChars.includes(firstChar)) {
    return <span className="text-3xl leading-none">{value}</span>;
  }

  if (lineChars.includes(firstChar) && value.length <= 2) {
    return <span className="text-3xl font-bold leading-none">{value}</span>;
  }

  if (/^\d+\/\d+$/.test(value)) {
    const size = value.length >= 5 ? 'text-xs' : 'text-sm';
    return <span className={`${size} font-bold leading-none whitespace-nowrap`}>{value}</span>;
  }

  if (/^-?\d*\.\d+$/.test(value)) {
    return <span className="text-sm font-bold leading-none whitespace-nowrap">{value}</span>;
  }

  if (/^-?\d+$/.test(value)) {
    const size = value.length >= 3 ? 'text-sm' : value.length === 2 ? 'text-xl' : 'text-3xl';
    return <span className={`${size} font-bold leading-none whitespace-nowrap`}>{value}</span>;
  }

  return <span className="text-sm leading-tight">{value}</span>;
}

/* ── 도형 시퀀스 (→ 화살표로 연결) ── */
function ShapeSequence({ data }: { data: VisualData }) {
  const items = data.items || [];
  return (
    <div className="flex flex-col items-center gap-2">
      {data.label && <span className="text-sm text-foreground-500">{data.label}</span>}
      <div className="flex items-center gap-3 flex-wrap justify-center">
        {items.map((item, idx) => (
          <div key={idx} className="flex items-center gap-3">
            <div className="w-20 h-20 flex items-center justify-center border-2 border-foreground-800 rounded-lg bg-background-50">
              <span className="text-4xl">{item === '?' ? '?' : renderCellContent(item)}</span>
            </div>
            {idx < items.length - 1 && (
              <i className="ri-arrow-right-line text-xl text-foreground-400"></i>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── 숫자 수열 (큰 숫자 가로 배치) ── */
function NumberSequence({ data }: { data: VisualData }) {
  const numbers = data.numbers || [];
  return (
    <div className="flex flex-col items-center gap-2">
      {data.label && <span className="text-sm text-foreground-500">{data.label}</span>}
      <div className="flex items-center gap-4 flex-wrap justify-center">
        {numbers.map((n, idx) => (
          <div key={idx} className="flex items-center gap-4">
            <div className="min-w-[60px] h-14 flex items-center justify-center">
              {n === '?' || n === null ? (
                <span className="text-3xl font-bold text-primary-600">?</span>
              ) : (
                <span className="text-3xl font-bold text-foreground-900">{n}</span>
              )}
            </div>
            {idx < numbers.length - 1 && (
              <span className="text-foreground-300 text-xl">,</span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── 숫자 그리드 (3×3 등) ── */
function NumberGrid({ data }: { data: VisualData }) {
  const cells = data.matrix || [];
  const cols = cells[0]?.length || 3;
  return (
    <div className="flex flex-col items-center gap-2">
      {data.label && <span className="text-sm text-foreground-500 mb-2">{data.label}</span>}
      <div
        className="grid gap-0 border-2 border-foreground-800"
        style={{ gridTemplateColumns: `repeat(${cols}, 72px)` }}
      >
        {cells.flat().map((cell, idx) => (
          <Cell key={idx} value={cell} size="lg" />
        ))}
      </div>
    </div>
  );
}

/* ── 회전 시퀀스 ── */
function Rotation({ data }: { data: VisualData }) {
  const items = data.items || [];
  const rotations = data.rotations || [];
  return (
    <div className="flex flex-col items-center gap-2">
      {data.label && <span className="text-sm text-foreground-500">{data.label}</span>}
      <div className="flex items-center gap-3 flex-wrap justify-center">
        {items.map((item, idx) => (
          <div key={idx} className="flex items-center gap-3">
            <div
              className="w-16 h-16 flex items-center justify-center border-2 border-foreground-800 rounded-lg bg-background-50"
              style={{ transform: rotations[idx] ? `rotate(${rotations[idx]}deg)` : undefined }}
            >
              <span className="text-3xl">{item === '?' ? '?' : item}</span>
            </div>
            {idx < items.length - 1 && (
              <i className="ri-arrow-right-line text-xl text-foreground-400"></i>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── 화살표 시퀀스 (회전 방향 등) ── */
function ArrowSequence({ data }: { data: VisualData }) {
  const items = data.items || [];
  return (
    <div className="flex flex-col items-center gap-2">
      {data.label && <span className="text-sm text-foreground-500">{data.label}</span>}
      <div className="flex items-center gap-3 flex-wrap justify-center">
        {items.map((item, idx) => (
          <div key={idx} className="flex items-center gap-3">
            <div className="w-16 h-16 flex items-center justify-center border-2 border-foreground-800 rounded-lg bg-background-50">
              <span className="text-3xl">{item === '?' ? '?' : item}</span>
            </div>
            {idx < items.length - 1 && (
              <i className="ri-arrow-right-line text-xl text-foreground-400"></i>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── 블록 그리드 (테트리스 조각·평면도 등) ── */
function BlockGrid({ data, compact }: { data: VisualData; compact: boolean }) {
  const grid = data.grid || [];
  const rows = grid.length;
  const cols = grid[0]?.length || 0;
  const size = compact ? 24 : 40;
  return (
    <div className="flex flex-col items-center gap-1">
      {data.label && <span className="text-sm text-foreground-500 mb-1">{data.label}</span>}
      <div
        className="grid gap-1"
        style={{ gridTemplateColumns: `repeat(${cols}, ${size}px)`, gridTemplateRows: `repeat(${rows}, ${size}px)` }}
      >
        {grid.flat().map((cell, idx) => (
          <div
            key={idx}
            className={cell ? 'bg-primary-500 rounded-[4px]' : 'bg-background-100 rounded-[4px] border border-background-200'}
            style={{ width: size, height: size }}
          />
        ))}
      </div>
    </div>
  );
}

/* ── 도형 겹치기 (A / B / 겹친 부분) ── */
function Overlap({ data, compact }: { data: VisualData; compact: boolean }) {
  const grid = data.grid || [];
  const rows = grid.length;
  const cols = grid[0]?.length || 0;
  const size = compact ? 24 : 40;

  const colorFor = (v: number) => {
    if (v === 1) return 'bg-primary-400';
    if (v === 2) return 'bg-accent-400';
    if (v === 3) return 'bg-foreground-800';
    return 'bg-background-100';
  };

  return (
    <div className="flex flex-col items-center gap-2">
      {data.label && <span className="text-sm text-foreground-500 mb-1">{data.label}</span>}
      <div
        className="grid gap-1"
        style={{ gridTemplateColumns: `repeat(${cols}, ${size}px)`, gridTemplateRows: `repeat(${rows}, ${size}px)` }}
      >
        {grid.flat().map((cell, idx) => (
          <div
            key={idx}
            className={`${colorFor(cell)} rounded-[4px]`}
            style={{ width: size, height: size }}
          />
        ))}
      </div>
      <div className="flex items-center gap-3 text-xs text-foreground-600 mt-1">
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 bg-primary-400 rounded-[2px] inline-block"></span> A 도형
        </span>
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 bg-accent-400 rounded-[2px] inline-block"></span> B 도형
        </span>
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 bg-foreground-800 rounded-[2px] inline-block"></span> 겹친 부분
        </span>
      </div>
    </div>
  );
}

/* ── 도형 겹치기 매트릭스 (3×3 표, 각 칸이 작은 도형) ── */
function OverlapMatrix({ data }: { data: VisualData }) {
  const cells = data.cells || [];
  const size = 15;
  return (
    <div className="flex flex-col items-center gap-1">
      {data.label && <span className="text-sm text-foreground-500 mb-2">{data.label}</span>}
      <div className="grid gap-2" style={{ gridTemplateColumns: 'repeat(3, auto)' }}>
        {cells.map((cell, idx) => (
          <div
            key={idx}
            className="w-[76px] h-[76px] flex items-center justify-center border-2 border-foreground-800 rounded-lg bg-background-50"
          >
            {cell === null ? (
              <span className="text-2xl font-bold text-primary-600">?</span>
            ) : (
              <MiniGrid grid={cell} size={size} />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function MiniGrid({ grid, size }: { grid: number[][]; size: number }) {
  const rows = grid.length;
  const cols = grid[0]?.length || 0;
  return (
    <div
      className="grid gap-[2px]"
      style={{ gridTemplateColumns: `repeat(${cols}, ${size}px)`, gridTemplateRows: `repeat(${rows}, ${size}px)` }}
    >
      {grid.flat().map((cell, idx) => (
        <div
          key={idx}
          className={cell ? 'bg-primary-500 rounded-[2px]' : 'bg-background-100 rounded-[2px] border border-background-200'}
          style={{ width: size, height: size }}
        />
      ))}
    </div>
  );
}

/* ── 단일 3차원 입체 (복셀 리스트) ── */
function SolidView({ voxels, size = 12 }: { voxels: number[][]; size?: number }) {
  if (!voxels || voxels.length === 0) return null;
  const s = size;
  const sorted = [...voxels].sort((a, b) => (a[0] + a[1]) - (b[0] + b[1]) || a[2] - b[2]);
  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;
  const cubes = sorted.map(([x, y, z]) => {
    const gx = (x - y) * s;
    const gy = (x + y) * (s / 2);
    const left = gx - s;
    const top = gy - z * s - 1.5 * s;
    minX = Math.min(minX, left);
    maxX = Math.max(maxX, left + 2 * s);
    minY = Math.min(minY, top);
    maxY = Math.max(maxY, gy - z * s);
    return { x, y, z, gx, gy };
  });
  const width = maxX - minX;
  const height = maxY - minY;
  return (
    <div className="relative" style={{ width, height }}>
      {cubes.map((c, i) => {
        const left = c.gx - s - minX;
        const top = c.gy - c.z * s - 1.5 * s - minY;
        return (
          <div key={i} className="absolute" style={{ left, top, width: 2 * s, height: 1.5 * s }}>
            <div className="absolute bg-accent-300" style={{ left: 0, top: 0, width: 2 * s, height: s, clipPath: 'polygon(50% 0, 100% 50%, 50% 100%, 0 50%)' }} />
            <div className="absolute bg-accent-400" style={{ left: 0, top: s / 2, width: s, height: s, clipPath: 'polygon(0 0, 100% 50%, 100% 100%, 0 50%)' }} />
            <div className="absolute bg-accent-500" style={{ left: s, top: s / 2, width: s, height: s, clipPath: 'polygon(0 50%, 100% 0, 100% 50%, 0 100%)' }} />
          </div>
        );
      })}
    </div>
  );
}

/* ── 3차원 입체 겹치기 매트릭스 (3×3 표, 각 칸이 작은 3D 입체) ── */
function OverlapMatrix3D({ data }: { data: VisualData }) {
  const cells = data.solids || [];
  return (
    <div className="flex flex-col items-center gap-1">
      {data.label && <span className="text-sm text-foreground-500 mb-2">{data.label}</span>}
      <div className="grid gap-2" style={{ gridTemplateColumns: 'repeat(3, auto)' }}>
        {cells.map((cell, idx) => (
          <div key={idx} className="w-[88px] h-[88px] flex items-center justify-center border-2 border-foreground-800 rounded-lg bg-background-50">
            {cell === null ? (
              <span className="text-2xl font-bold text-primary-600">?</span>
            ) : (
              <SolidView voxels={cell} size={10} />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── 쌓기나무 (등축 투영) ── */
function CubeStack({ data, compact }: { data: VisualData; compact: boolean }) {
  const heights = data.heights || [];
  const s = compact ? 16 : 26;

  const rows = heights.length;
  const cols = Math.max(...heights.map((r) => r.length), 0);

  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;

  const cubes: { r: number; c: number; k: number }[] = [];

  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const h = heights[r]?.[c] || 0;
      for (let k = 0; k < h; k++) {
        const gx = (c - r) * s;
        const gy = (c + r) * (s / 2);
        const left = gx - s;
        const top = gy - k * s - 1.5 * s;
        minX = Math.min(minX, left);
        maxX = Math.max(maxX, left + 2 * s);
        minY = Math.min(minY, top);
        maxY = Math.max(maxY, gy - k * s);
        cubes.push({ r, c, k });
      }
    }
  }

  if (cubes.length === 0) return null;

  const width = maxX - minX;
  const height = maxY - minY;

  return (
    <div className="flex flex-col items-center gap-1">
      {data.label && <span className="text-sm text-foreground-500">{data.label}</span>}
      <div className="relative" style={{ width, height }}>
        {cubes.map((cube, i) => {
          const gx = (cube.c - cube.r) * s;
          const gy = (cube.c + cube.r) * (s / 2);
          const left = gx - s - minX;
          const top = gy - cube.k * s - 1.5 * s - minY;
          return (
            <div key={i} className="absolute" style={{ left, top, width: 2 * s, height: 1.5 * s }}>
              <div
                className="absolute bg-accent-300"
                style={{ left: 0, top: 0, width: 2 * s, height: s, clipPath: 'polygon(50% 0, 100% 50%, 50% 100%, 0 50%)' }}
              />
              <div
                className="absolute bg-accent-400"
                style={{ left: 0, top: s / 2, width: s, height: s, clipPath: 'polygon(0 0, 100% 50%, 100% 100%, 0 50%)' }}
              />
              <div
                className="absolute bg-accent-500"
                style={{ left: s, top: s / 2, width: s, height: s, clipPath: 'polygon(0 50%, 100% 0, 100% 50%, 0 100%)' }}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ── 막대 원형 매트릭스 ── */
function SpokeMatrix({ data }: { data: VisualData }) {
  const cells = data.spokes || [];
  return (
    <div className="flex flex-col items-center gap-1">
      <div className="grid gap-2" style={{ gridTemplateColumns: 'repeat(3, auto)' }}>
        {cells.map((cell, idx) => (
          <div key={idx} className="w-[76px] h-[76px] flex items-center justify-center border-2 border-foreground-800 rounded-lg bg-background-50">
            {cell === null ? (
              <span className="text-2xl font-bold text-primary-600">?</span>
            ) : (
              <SpokeCircleSVG count={cell} size={56} />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── 막대 원형 단일 ── */
function SpokeShape({ data }: { data: VisualData }) {
  const count = data.spokeCount ?? 0;
  return <SpokeCircleSVG count={count} size={48} />;
}

function SpokeCircleSVG({ count, size }: { count: number; size: number }) {
  const r = size / 2 - 3;
  const cx = size / 2;
  const cy = size / 2;
  return (
    <svg width={size} height={size} className="text-foreground-800">
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="currentColor" strokeWidth={2} />
      {count > 0 && Array.from({ length: count }, (_, i) => {
        const angle = (360 / count) * i - 90;
        const rad = (angle * Math.PI) / 180;
        return (
          <line
            key={i}
            x1={cx}
            y1={cy}
            x2={cx + r * Math.cos(rad)}
            y2={cy + r * Math.sin(rad)}
            stroke="currentColor"
            strokeWidth={2}
          />
        );
      })}
    </svg>
  );
}

/* ── 격자 선 매트릭스 ── */
function GridLineMatrix({ data }: { data: VisualData }) {
  const cells = data.crosses || [];
  return (
    <div className="flex flex-col items-center gap-1">
      <div className="grid gap-2" style={{ gridTemplateColumns: 'repeat(3, auto)' }}>
        {cells.map((cell, idx) => (
          <div key={idx} className="w-[76px] h-[76px] flex items-center justify-center border-2 border-foreground-800 rounded-lg bg-background-50">
            {cell === null ? (
              <span className="text-2xl font-bold text-primary-600">?</span>
            ) : (
              <GridLineSVG h={cell.h} v={cell.v} size={56} />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── 격자 선 단일 ── */
function GridLineShape({ data }: { data: VisualData }) {
  const h = data.cross?.h ?? 0;
  const v = data.cross?.v ?? 0;
  return <GridLineSVG h={h} v={v} size={48} />;
}

function GridLineSVG({ h, v, size }: { h: number; v: number; size: number }) {
  return (
    <svg width={size} height={size} className="text-foreground-800">
      <rect x={2} y={2} width={size - 4} height={size - 4} fill="none" stroke="currentColor" strokeWidth={2} />
      {h > 0 && Array.from({ length: h }, (_, i) => {
        const y = 2 + ((i + 1) / (h + 1)) * (size - 4);
        return <line key={`h${i}`} x1={2} y1={y} x2={size - 2} y2={y} stroke="currentColor" strokeWidth={2} />;
      })}
      {v > 0 && Array.from({ length: v }, (_, i) => {
        const x = 2 + ((i + 1) / (v + 1)) * (size - 4);
        return <line key={`v${i}`} x1={x} y1={2} x2={x} y2={size - 2} stroke="currentColor" strokeWidth={2} />;
      })}
    </svg>
  );
}

/* ── 줄무늬 도형 매트릭스 ── */
function StripeMatrix({ data }: { data: VisualData }) {
  const cells = data.stripes || [];
  return (
    <div className="flex flex-col items-center gap-1">
      <div className="grid gap-2" style={{ gridTemplateColumns: 'repeat(3, auto)' }}>
        {cells.map((cell, idx) => (
          <div key={idx} className="w-[76px] h-[76px] flex items-center justify-center border-2 border-foreground-800 rounded-lg bg-background-50">
            {cell === null ? (
              <span className="text-2xl font-bold text-primary-600">?</span>
            ) : (
              <StripeDiv shape={cell.shape} dir={cell.dir} size={56} />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── 줄무늬 도형 단일 ── */
function StripeShape({ data }: { data: VisualData }) {
  const shape = data.stripe?.shape ?? 'square';
  const dir = data.stripe?.dir ?? 'none';
  return <StripeDiv shape={shape} dir={dir} size={48} />;
}

function StripeDiv({ shape, dir, size }: { shape: string; dir: string; size: number }) {
  const borderRadius = shape === 'circle' ? '50%' : '0';
  let background = 'none';
  if (dir === '/') {
    background = 'repeating-linear-gradient(45deg, transparent, transparent 5px, currentColor 5px, currentColor 7px)';
  } else if (dir === '\\') {
    background = 'repeating-linear-gradient(-45deg, transparent, transparent 5px, currentColor 5px, currentColor 7px)';
  } else if (dir === '|') {
    background = 'repeating-linear-gradient(90deg, transparent, transparent 5px, currentColor 5px, currentColor 7px)';
  } else if (dir === '-') {
    background = 'repeating-linear-gradient(0deg, transparent, transparent 5px, currentColor 5px, currentColor 7px)';
  }

  return (
    <div
      className="border-2 border-foreground-800 text-foreground-800 flex-shrink-0"
      style={{
        width: size,
        height: size,
        borderRadius,
        background,
      }}
    />
  );
}

/* ── XOR 선 매트릭스 ── */
function XorLineMatrix({ data }: { data: VisualData }) {
  const cells = data.xorLines || [];
  return (
    <div className="flex flex-col items-center gap-1">
      <div className="grid gap-2" style={{ gridTemplateColumns: 'repeat(3, auto)' }}>
        {cells.map((cell, idx) => (
          <div key={idx} className="w-[76px] h-[76px] flex items-center justify-center border-2 border-foreground-800 rounded-lg bg-background-50">
            {cell === null ? (
              <span className="text-2xl font-bold text-primary-600">?</span>
            ) : (
              <XorShapeSVG shape={cell.shape} lines={cell.lines} size={56} />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── XOR 선 단일 ── */
function XorLineShape({ data }: { data: VisualData }) {
  const shape = data.xorLine?.shape ?? 'square';
  const lines = data.xorLine?.lines ?? [];
  return <XorShapeSVG shape={shape} lines={lines} size={48} />;
}

function XorShapeSVG({ shape, lines, size }: { shape: string; lines: string[]; size: number }) {
  const hasSlash = lines.includes('/') || lines.includes('x');
  const hasBackslash = lines.includes('\\') || lines.includes('x');
  const r = shape === 'circle' ? size / 2 - 3 : 0;

  return (
    <svg width={size} height={size} className="text-foreground-800">
      {shape === 'circle' ? (
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="currentColor" strokeWidth={2} />
      ) : (
        <rect x={2} y={2} width={size - 4} height={size - 4} fill="none" stroke="currentColor" strokeWidth={2} />
      )}
      {hasSlash && (
        <line x1={4} y1={size - 4} x2={size - 4} y2={4} stroke="currentColor" strokeWidth={2} />
      )}
      {hasBackslash && (
        <line x1={4} y1={4} x2={size - 4} y2={size - 4} stroke="currentColor" strokeWidth={2} />
      )}
    </svg>
  );
}

/* ── 복합 도형 (외곽 프레임 + 내부 선) ── */
function polygonPoints(cx: number, cy: number, r: number, n: number): string {
  const pts: string[] = [];
  for (let i = 0; i < n; i++) {
    const a = ((-90 + i * (360 / n)) * Math.PI) / 180;
    pts.push(`${cx + r * Math.cos(a)},${cy + r * Math.sin(a)}`);
  }
  return pts.join(' ');
}

function CompositeSVG({ frame, lines, size }: { frame: string; lines: string[]; size: number }) {
  const cx = size / 2;
  const cy = size / 2;
  const pad = 6;
  const r = size / 2 - pad;
  const p1 = pad + 4;
  const p2 = size - pad - 4;

  const expanded = lines.flatMap((l) => {
    if (l === 'x') return ['/', '\\'];
    if (l === '+') return ['h', 'v'];
    return [l];
  });

  let framePath: React.ReactNode = null;
  switch (frame) {
    case 'square':
      framePath = (
        <rect x={pad} y={pad} width={size - 2 * pad} height={size - 2 * pad} fill="none" stroke="currentColor" strokeWidth={2} />
      );
      break;
    case 'circle':
      framePath = (
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="currentColor" strokeWidth={2} />
      );
      break;
    case 'diamond':
      framePath = (
        <polygon points={`${cx},${pad} ${size - pad},${cy} ${cx},${size - pad} ${pad},${cy}`} fill="none" stroke="currentColor" strokeWidth={2} />
      );
      break;
    case 'triangle':
      framePath = (
        <polygon points={`${cx},${pad} ${size - pad},${size - pad} ${pad},${size - pad}`} fill="none" stroke="currentColor" strokeWidth={2} />
      );
      break;
    case 'pentagon':
      framePath = (
        <polygon points={polygonPoints(cx, cy, r, 5)} fill="none" stroke="currentColor" strokeWidth={2} />
      );
      break;
    case 'hexagon':
      framePath = (
        <polygon points={polygonPoints(cx, cy, r, 6)} fill="none" stroke="currentColor" strokeWidth={2} />
      );
      break;
    default:
      break;
  }

  const lineEls: React.ReactNode[] = [];
  expanded.forEach((line, i) => {
    switch (line) {
      case 'h':
        lineEls.push(<line key={`h${i}`} x1={p1} y1={cy} x2={p2} y2={cy} stroke="currentColor" strokeWidth={2} />);
        break;
      case 'v':
        lineEls.push(<line key={`v${i}`} x1={cx} y1={p1} x2={cx} y2={p2} stroke="currentColor" strokeWidth={2} />);
        break;
      case '/':
        lineEls.push(<line key={`s${i}`} x1={p1} y1={p2} x2={p2} y2={p1} stroke="currentColor" strokeWidth={2} />);
        break;
      case '\\':
        lineEls.push(<line key={`b${i}`} x1={p1} y1={p1} x2={p2} y2={p2} stroke="currentColor" strokeWidth={2} />);
        break;
      case 'grid2': {
        const gap = (p2 - p1) / 3;
        lineEls.push(<line key={`g2h1${i}`} x1={p1} y1={p1 + gap} x2={p2} y2={p1 + gap} stroke="currentColor" strokeWidth={1.5} />);
        lineEls.push(<line key={`g2h2${i}`} x1={p1} y1={p1 + 2 * gap} x2={p2} y2={p1 + 2 * gap} stroke="currentColor" strokeWidth={1.5} />);
        lineEls.push(<line key={`g2v1${i}`} x1={p1 + gap} y1={p1} x2={p1 + gap} y2={p2} stroke="currentColor" strokeWidth={1.5} />);
        lineEls.push(<line key={`g2v2${i}`} x1={p1 + 2 * gap} y1={p1} x2={p1 + 2 * gap} y2={p2} stroke="currentColor" strokeWidth={1.5} />);
        break;
      }
      case 'concentric':
        lineEls.push(<circle key={`cc${i}`} cx={cx} cy={cy} r={r * 0.55} fill="none" stroke="currentColor" strokeWidth={1.5} />);
        break;
      default:
        break;
    }
  });

  return (
    <svg width={size} height={size} className="text-foreground-800">
      {framePath}
      {lineEls}
    </svg>
  );
}

function CompositeMatrix({ data }: { data: VisualData }) {
  const cells = data.composites || [];
  return (
    <div className="flex flex-col items-center gap-1">
      <div className="grid gap-2" style={{ gridTemplateColumns: 'repeat(3, auto)' }}>
        {cells.map((cell, idx) => (
          <div key={idx} className="w-[76px] h-[76px] flex items-center justify-center border-2 border-foreground-800 rounded-lg bg-background-50">
            {cell === null ? (
              <span className="text-2xl font-bold text-primary-600">?</span>
            ) : (
              <CompositeSVG frame={cell.frame} lines={cell.lines} size={56} />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function CompositeShape({ data }: { data: VisualData }) {
  const frame = data.composite?.frame ?? 'square';
  const lines = data.composite?.lines ?? [];
  return <CompositeSVG frame={frame} lines={lines} size={48} />;
}

/* ── 아날로그 시계 (시침·분침) ── */
function ClockFaceSVG({ hour, minute, size }: { hour: number; minute: number; size: number }) {
  const cx = size / 2;
  const cy = size / 2;
  const r = size / 2 - 3;
  const hourAngle = ((hour % 12) + minute / 60) * 30;
  const minuteAngle = minute * 6;

  const point = (angle: number, length: number) => {
    const rad = (angle * Math.PI) / 180;
    return { x: cx + length * Math.sin(rad), y: cy - length * Math.cos(rad) };
  };

  const hp = point(hourAngle, r * 0.5);
  const mp = point(minuteAngle, r * 0.82);

  return (
    <svg width={size} height={size} className="text-foreground-800">
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="currentColor" strokeWidth={2} />
      {[0, 30, 60, 90, 120, 150, 180, 210, 240, 270, 300, 330].map((a) => {
        const outer = point(a, r - 2);
        const inner = point(a, r - (a % 90 === 0 ? 6 : 4));
        return (
          <line key={a} x1={inner.x} y1={inner.y} x2={outer.x} y2={outer.y} stroke="currentColor" strokeWidth={a % 90 === 0 ? 2.5 : 1} />
        );
      })}
      <line x1={cx} y1={cy} x2={hp.x} y2={hp.y} stroke="currentColor" strokeWidth={2.5} />
      <line x1={cx} y1={cy} x2={mp.x} y2={mp.y} stroke="currentColor" strokeWidth={1.5} />
      <circle cx={cx} cy={cy} r={2} fill="currentColor" />
    </svg>
  );
}

function ClockSequence({ data, compact }: { data: VisualData; compact: boolean }) {
  const clocks = data.clocks || [];
  const size = compact ? 48 : 56;
  return (
    <div className="flex flex-col items-center gap-2">
      {data.label && <span className="text-sm text-foreground-500">{data.label}</span>}
      <div className="flex items-center gap-3 flex-wrap justify-center">
        {clocks.map((c, idx) => (
          <div key={idx} className="flex items-center gap-3">
            <div className="w-20 h-20 flex items-center justify-center border-2 border-foreground-800 rounded-lg bg-background-50">
              {c === null ? (
                <span className="text-2xl font-bold text-primary-600">?</span>
              ) : (
                <ClockFaceSVG hour={c.hour} minute={c.minute} size={size} />
              )}
            </div>
            {idx < clocks.length - 1 && <i className="ri-arrow-right-line text-xl text-foreground-400"></i>}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── 정다면체 와이어프레임 (정육면체·정팔면체·정십이면체·정이십면체) ── */
function PlatonicSolidSVG({ solid, size }: { solid: string; size: number }) {
  const stroke = 'currentColor';
  const sw = 2;
  let content: React.ReactNode = null;

  if (solid === 'cube') {
    content = (
      <>
        <polygon points="18,16 46,16 46,44 18,44" fill="none" stroke={stroke} strokeWidth={sw} />
        <polygon points="10,24 38,24 38,52 10,52" fill="none" stroke={stroke} strokeWidth={sw} />
        <line x1="10" y1="24" x2="18" y2="16" stroke={stroke} strokeWidth={sw} />
        <line x1="38" y1="24" x2="46" y2="16" stroke={stroke} strokeWidth={sw} />
        <line x1="38" y1="52" x2="46" y2="44" stroke={stroke} strokeWidth={sw} />
        <line x1="10" y1="52" x2="18" y2="44" stroke={stroke} strokeWidth={sw} />
      </>
    );
  } else if (solid === 'octahedron') {
    content = (
      <>
        <polygon points="14,32 32,20 50,32 32,44" fill="none" stroke={stroke} strokeWidth={sw} />
        <line x1="32" y1="6" x2="14" y2="32" stroke={stroke} strokeWidth={sw} />
        <line x1="32" y1="6" x2="50" y2="32" stroke={stroke} strokeWidth={sw} />
        <line x1="32" y1="6" x2="32" y2="20" stroke={stroke} strokeWidth={sw} />
        <line x1="32" y1="6" x2="32" y2="44" stroke={stroke} strokeWidth={sw} />
        <line x1="32" y1="58" x2="14" y2="32" stroke={stroke} strokeWidth={sw} />
        <line x1="32" y1="58" x2="50" y2="32" stroke={stroke} strokeWidth={sw} />
        <line x1="32" y1="58" x2="32" y2="20" stroke={stroke} strokeWidth={sw} />
        <line x1="32" y1="58" x2="32" y2="44" stroke={stroke} strokeWidth={sw} />
      </>
    );
  } else if (solid === 'dodecahedron') {
    content = (
      <>
        <polygon points="32,10 52,24 44,46 20,46 12,24" fill="none" stroke={stroke} strokeWidth={sw} />
        <polygon points="32,22 44,30 39,44 25,44 20,30" fill="none" stroke={stroke} strokeWidth={sw} />
        <line x1="32" y1="10" x2="32" y2="22" stroke={stroke} strokeWidth={sw} />
        <line x1="52" y1="24" x2="44" y2="30" stroke={stroke} strokeWidth={sw} />
        <line x1="44" y1="46" x2="39" y2="44" stroke={stroke} strokeWidth={sw} />
        <line x1="20" y1="46" x2="25" y2="44" stroke={stroke} strokeWidth={sw} />
        <line x1="12" y1="24" x2="20" y2="30" stroke={stroke} strokeWidth={sw} />
      </>
    );
  } else if (solid === 'icosahedron') {
    content = (
      <>
        <polygon points="32,6 54,54 10,54" fill="none" stroke={stroke} strokeWidth={sw} />
        <polygon points="32,22 43,44 21,44" fill="none" stroke={stroke} strokeWidth={sw} />
        <line x1="32" y1="6" x2="32" y2="22" stroke={stroke} strokeWidth={sw} />
        <line x1="32" y1="6" x2="43" y2="44" stroke={stroke} strokeWidth={sw} />
        <line x1="32" y1="6" x2="21" y2="44" stroke={stroke} strokeWidth={sw} />
        <line x1="54" y1="54" x2="43" y2="44" stroke={stroke} strokeWidth={sw} />
        <line x1="10" y1="54" x2="21" y2="44" stroke={stroke} strokeWidth={sw} />
        <line x1="54" y1="54" x2="32" y2="22" stroke={stroke} strokeWidth={sw} />
        <line x1="10" y1="54" x2="32" y2="22" stroke={stroke} strokeWidth={sw} />
      </>
    );
  }

  return (
    <svg width={size} height={size} viewBox="0 0 64 64" className="text-foreground-800">
      {content}
    </svg>
  );
}

function SolidSequence({ data, compact }: { data: VisualData; compact: boolean }) {
  const solids = data.solids || [];
  const size = compact ? 44 : 52;
  return (
    <div className="flex flex-col items-center gap-2">
      {data.label && <span className="text-sm text-foreground-500">{data.label}</span>}
      <div className="flex items-center gap-3 flex-wrap justify-center">
        {solids.map((s, idx) => (
          <div key={idx} className="flex items-center gap-3">
            <div className="w-20 h-20 flex items-center justify-center border-2 border-foreground-800 rounded-lg bg-background-50">
              {s === null ? (
                <span className="text-2xl font-bold text-primary-600">?</span>
              ) : (
                <PlatonicSolidSVG solid={s} size={size} />
              )}
            </div>
            {idx < solids.length - 1 && <i className="ri-arrow-right-line text-xl text-foreground-400"></i>}
          </div>
        ))}
      </div>
    </div>
  );
}

function SolidMatrix({ data }: { data: VisualData }) {
  const solids = data.solids || [];
  return (
    <div className="flex flex-col items-center gap-1">
      {data.label && <span className="text-sm text-foreground-500 mb-2">{data.label}</span>}
      <div className="grid gap-2" style={{ gridTemplateColumns: 'repeat(3, auto)' }}>
        {solids.map((s, idx) => (
          <div key={idx} className="w-[76px] h-[76px] flex items-center justify-center border-2 border-foreground-800 rounded-lg bg-background-50">
            {s === null ? (
              <span className="text-2xl font-bold text-primary-600">?</span>
            ) : (
              <PlatonicSolidSVG solid={s} size={46} />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── 포커 문양 회전 정다면체 (정육면체·정팔면체) ── */
const POKER_SUIT_COLORS: Record<string, string> = {
  '♠': '#1f2937',
  '♣': '#1f2937',
  '♥': '#dc2626',
  '♦': '#dc2626',
};

function SuitText({ suit, x, y, size }: { suit: string; x: number; y: number; size: number }) {
  return (
    <text
      x={x}
      y={y}
      textAnchor="middle"
      dominantBaseline="central"
      fontSize={size}
      fontWeight={700}
      fill={POKER_SUIT_COLORS[suit] || '#1f2937'}
      className="select-none"
    >
      {suit}
    </text>
  );
}

function CubeFaceOn({ front, right, top, size }: { front: string; right: string; top: string; size: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 100 100" className="text-foreground-700">
      <polygon points="28,32 68,32 84,16 44,16" fill="#f3f4f6" stroke="currentColor" strokeWidth={2} />
      <polygon points="68,32 68,72 84,56 84,16" fill="#e5e7eb" stroke="currentColor" strokeWidth={2} />
      <polygon points="28,32 68,32 68,72 28,72" fill="#ffffff" stroke="currentColor" strokeWidth={2} />
      <SuitText suit={top} x={56} y={24} size={12} />
      <SuitText suit={right} x={76} y={44} size={12} />
      <SuitText suit={front} x={48} y={54} size={22} />
    </svg>
  );
}

function CubeCorner({ front, right, top, size }: { front: string; right: string; top: string; size: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 100 100" className="text-foreground-700">
      <polygon points="50,4 80,21 50,38 20,21" fill="#f3f4f6" stroke="currentColor" strokeWidth={2} />
      <polygon points="20,21 50,38 50,72 20,55" fill="#ffffff" stroke="currentColor" strokeWidth={2} />
      <polygon points="50,38 80,21 80,55 50,72" fill="#e5e7eb" stroke="currentColor" strokeWidth={2} />
      <SuitText suit={top} x={50} y={21} size={11} />
      <SuitText suit={front} x={35} y={46} size={15} />
      <SuitText suit={right} x={65} y={46} size={15} />
    </svg>
  );
}

function OctahedronView({ f0, f1, f2, f3, size }: { f0: string; f1: string; f2: string; f3: string; size: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 100 100" className="text-foreground-700">
      <polygon points="50,6 94,50 50,50" fill="#ffffff" stroke="currentColor" strokeWidth={1.5} />
      <polygon points="94,50 50,94 50,50" fill="#e5e7eb" stroke="currentColor" strokeWidth={1.5} />
      <polygon points="50,94 6,50 50,50" fill="#f3f4f6" stroke="currentColor" strokeWidth={1.5} />
      <polygon points="6,50 50,6 50,50" fill="#fafafa" stroke="currentColor" strokeWidth={1.5} />
      <SuitText suit={f0} x={65} y={34} size={13} />
      <SuitText suit={f1} x={65} y={66} size={13} />
      <SuitText suit={f2} x={35} y={66} size={13} />
      <SuitText suit={f3} x={35} y={34} size={13} />
    </svg>
  );
}

function PokerSolidView({
  solid,
  faces,
  top,
  step,
  size,
}: {
  solid: string;
  faces: string[];
  top: string;
  step: number;
  size: number;
}) {
  if (solid === 'octahedron') {
    const k = Math.floor(step / 2) % 4;
    return (
      <OctahedronView
        f0={faces[k % 4]}
        f1={faces[(k + 1) % 4]}
        f2={faces[(k + 2) % 4]}
        f3={faces[(k + 3) % 4]}
        size={size}
      />
    );
  }
  const frontIdx = Math.floor(step / 2) % 4;
  const rightIdx = (frontIdx + 1) % 4;
  const isCorner = step % 2 === 1;
  const front = faces[frontIdx];
  const right = faces[rightIdx];
  if (isCorner) {
    return <CubeCorner front={front} right={right} top={top} size={size} />;
  }
  return <CubeFaceOn front={front} right={right} top={top} size={size} />;
}

function PokerRotation({ data, compact }: { data: VisualData; compact: boolean }) {
  const solid = data.pokerSolid ?? 'cube';
  const faces = data.faces ?? ['♠', '♥', '♦', '♣'];
  const top = data.top ?? '♠';
  const rotations = data.rotations ?? [];
  const size = compact ? 52 : 60;

  let step = 0;
  const steps: number[] = [0];
  for (const r of rotations) {
    step = (step + Math.round(r / 45)) % 8;
    steps.push(step);
  }
  const knownSteps = steps.slice(0, -1);

  return (
    <div className="flex flex-col items-center gap-2">
      {data.label && <span className="text-sm text-foreground-500">{data.label}</span>}
      <div className="flex items-center gap-3 flex-wrap justify-center">
        {knownSteps.map((s, idx) => (
          <div key={idx} className="flex items-center gap-3">
            <div className="w-20 h-20 flex items-center justify-center border-2 border-foreground-800 rounded-lg bg-background-50">
              <PokerSolidView solid={solid} faces={faces} top={top} step={s} size={size} />
            </div>
            <i className="ri-arrow-right-line text-xl text-foreground-400"></i>
          </div>
        ))}
        <div className="w-20 h-20 flex items-center justify-center border-2 border-foreground-800 rounded-lg bg-primary-50/60">
          <span className="text-2xl font-bold text-primary-600">?</span>
        </div>
      </div>
    </div>
  );
}

function PokerSolid({ data, compact }: { data: VisualData; compact: boolean }) {
  const solid = data.pokerSolid ?? 'cube';
  const faces = data.faces ?? ['♠', '♥', '♦', '♣'];
  const top = data.top ?? '♠';
  const step = data.step ?? 0;
  const size = compact ? 52 : 60;
  return <PokerSolidView solid={solid} faces={faces} top={top} step={step} size={size} />;
}